/**
 * @file Service worker registration for Uppy GoldenRetriever large-file blob recovery.
 */

export const UPLOAD_SERVICE_WORKER_FILENAME = 'uppy-golden-retriever-sw.js';

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null =
  null;

export type UploadServiceWorkerRegistration = {
  swUrl: string;
  scope: string;
};

/**
 * Resolves SW script URL and scope for the current page.
 * CDN-hosted preview/production builds must register on the app origin, not the CDN.
 */
export const resolveUploadServiceWorkerRegistration = (
  baseUrl: string,
  pageOrigin: string
): UploadServiceWorkerRegistration => {
  const resolvedBase = new URL(baseUrl, `${pageOrigin}/`);
  const isCrossOrigin = resolvedBase.origin !== pageOrigin;

  if (isCrossOrigin) {
    return {
      swUrl: `/${UPLOAD_SERVICE_WORKER_FILENAME}`,
      scope: '/',
    };
  }

  return {
    swUrl: `${baseUrl}${UPLOAD_SERVICE_WORKER_FILENAME}`,
    scope: baseUrl,
  };
};

/** @internal Resets cached registration for unit tests. */
export const resetUploadServiceWorkerRegistrationForTests = (): void => {
  registrationPromise = null;
};

/** Register GoldenRetriever's service worker for large-file blob recovery on reload. */
export const registerUploadServiceWorker =
  (): Promise<ServiceWorkerRegistration | null> => {
    if (registrationPromise) {
      return registrationPromise;
    }

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      registrationPromise = Promise.resolve(null);
      return registrationPromise;
    }

    const { swUrl, scope } = resolveUploadServiceWorkerRegistration(
      import.meta.env.BASE_URL,
      window.location.origin
    );

    registrationPromise = navigator.serviceWorker
      .register(swUrl, { scope })
      .then((registration) =>
        navigator.serviceWorker.ready.then(() => registration)
      )
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[Uploads] Service worker registration failed:', error);
        return null;
      });

    return registrationPromise;
  };
